import React, { memo, useMemo, useCallback, Fragment } from 'react';
import cx from 'clsx';
import { extractUrlsIndices } from '@debank/content-editor';
import { useHistory } from 'react-router-dom';

import { HelperTooltip, UserCardPopup, Translated, useTranslateOpen } from '@/components';
import { useCurrentAddressInfo, useUrl } from '@/hooks';
import type { FeedArticle, FeedArticleEntity } from '@/views/Stream';
import {
  isOfficalAddress,
  withHttp,
  getUrlWithEllipsis,
  openNewWindow,
  openOfficialById,
} from '@/utils';

import { omitRefCode } from '../../ReferralCodeBtn/utils';
import style from './index.module.scss';

const MAX_URL_LENGTH_IN_PREVIEW = 26;

type EntityType = {
  tag: string;
  indices: [number, number];
  url?: string;
  display_url?: string;
  id?: string;
  redirect_to?: string;
  raw_string?: string;
};

const SUPPORTED_RICHTEXT_ENTITIES = ['url', 'mentions', 'tags', 'channels'];

type ArticleEntityContentProps = {
  article: Pick<
    FeedArticle,
    'content' | 'entities' | 'content_translation' | 'entities_translation' | 'creator_id'
  >;
  maxDisplay?: number;
  openUrl: (url: string) => void;
};

const MemoArticleEntityContent = memo(
  ({ article, maxDisplay, openUrl }: ArticleEntityContentProps) => {
    const showTranslated = useTranslateOpen();
    // 用字符串分割文本，与后端长度计算统一
    const articleArr = useMemo(
      () =>
        showTranslated && article.content_translation
          ? [...article.content_translation]
          : [...article.content],
      [article, showTranslated],
    );

    const articleEntities = useMemo(
      () =>
        showTranslated && article.entities_translation
          ? article.entities_translation
          : article.entities,
      [article, showTranslated],
    );

    const history = useHistory();

    const _content = useMemo(() => {
      if (maxDisplay) {
        return articleArr.slice(0, maxDisplay);
      }
      return articleArr;
    }, [articleArr, maxDisplay]);

    const showViewFull = useMemo(() => {
      if (!maxDisplay || !articleArr?.length) {
        return false;
      }
      return articleArr.length > maxDisplay;
    }, [maxDisplay, articleArr]);

    const [entities, richtext] = useMemo(() => {
      const _entities: EntityType[] = [];
      const _richtext: EntityType[] = [];

      if (!articleEntities) {
        return [_entities, _richtext];
      }

      try {
        const kvEntities = Object.entries(articleEntities);
        for (let i = 0; i < kvEntities.length; i++) {
          const e = kvEntities[i];

          if (e.length) {
            if (e[0] === 'richtext') {
              const eachRichtext = (e[1] as any)
                .filter((x: EntityType) => !maxDisplay || !(x.indices[0] > maxDisplay))
                .map((x: any) => ({
                  tag: x.type,
                  ...x,
                }));
              _richtext.push(...eachRichtext);
            } else if (SUPPORTED_RICHTEXT_ENTITIES.includes(e[0])) {
              _entities.push(
                ...(e[1] as any)
                  .filter((x: EntityType) => !maxDisplay || !(x.indices[0] > maxDisplay))
                  .map((x: any) => ({
                    tag: e[0],
                    ...x,
                  })),
              );
            }
          }
        }

        _entities.sort((m, n) => m.indices[0] - n.indices[0]);
        _richtext.sort((m, n) => m.indices[0] - n.indices[0]);
      } catch (e) {
        console.warn('invalid entities', e);
        return [[], []];
      }

      return [_entities, _richtext];
    }, [articleEntities, maxDisplay]);

    const handleUrlEntityClick = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        e.preventDefault();

        const target = e.currentTarget.dataset?.target;

        if (target) {
          openUrl(target);
        }
      },
      [openUrl],
    );

    const handlerSearchClick = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        e.preventDefault();
        const keyword = e.currentTarget.dataset?.target || '';
        history.push(`/?tab=search&q=${encodeURIComponent(keyword)}`, {
          gaAction: 'Search_Tag_Click',
        });
      },
      [history],
    );
    const { address } = useCurrentAddressInfo();

    const openProfilePage = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        e.preventDefault();
        const id = String(e.currentTarget.dataset?.target) || '';
        if (isOfficalAddress(id)) {
          openOfficialById({ address, officialId: id });
        } else {
          openNewWindow(`/profile/${id}`, '_blank');
        }
      },
      [address],
    );

    const openChannelPage = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        e.preventDefault();
        const id = String(e.currentTarget.dataset?.id) || '';
        history.push(`/channel/${id}`);
      },
      [history],
    );

    const renderEntity = useCallback(
      (data: EntityType, text: string[]) => {
        const commonText = (
          <span>
            <ArticleStyledContent text={text} offset={data.indices[0]} richtext={richtext} />
          </span>
        );
        if (data.tag === 'url') {
          return (
            <HelperTooltip
              title={data.url}
              disable={!data.url || data.url.length <= MAX_URL_LENGTH_IN_PREVIEW}
              mouseEnterDelay={0.3}>
              <span
                data-target={data.url}
                onClick={data.url ? handleUrlEntityClick : undefined}
                className={data.url ? style.entityLink : undefined}>
                {/* <ArticleStyledContent text={text} offset={data.indices[0]} richtext={richtext} /> */}
                {getUrlWithEllipsis(data.url, MAX_URL_LENGTH_IN_PREVIEW)}
              </span>
            </HelperTooltip>
          );
        } else if (data.tag === 'mentions') {
          return (
            <span
              data-target={data.id}
              onClick={data.id ? openProfilePage : undefined}
              className={isOfficalAddress(String(data.id)) ? style.officialMention : style.mention}>
              <UserCardPopup address={String(data.id)}>{data.raw_string}</UserCardPopup>
            </span>
          );
        } else if (data.tag === 'tags') {
          if (data.id !== data.raw_string?.slice(1)?.toLowerCase()) {
            return commonText;
          }
          return (
            <span
              data-target={`${data.raw_string?.slice(0, 1)}${data.redirect_to || data.id}`}
              data-type={data.tag}
              onClick={data.raw_string ? handlerSearchClick : undefined}
              className={style.tag}>
              {data.raw_string}
            </span>
          );
        } else if (data.tag === 'channels') {
          if (data.id !== data.raw_string?.slice(1)?.toLowerCase()) {
            return commonText;
          }
          return (
            <span
              data-id={data.id}
              onClick={data.id ? openChannelPage : undefined}
              className={style.officialMention}>
              {data.raw_string}
            </span>
          );
        }
        return commonText;
      },
      [handleUrlEntityClick, handlerSearchClick, openProfilePage, openChannelPage, richtext],
    );

    return (
      <>
        {entities.length ? (
          <>
            {entities[0].indices[0] ? (
              <ArticleStyledContent
                offset={0}
                text={_content.slice(0, entities[0].indices[0])}
                richtext={richtext}
              />
            ) : null}
            {entities.map((x, i) => {
              const text = _content.slice(x.indices[0], x.indices[1]);

              return (
                <Fragment key={`${x.tag}_${i}`}>
                  {renderEntity(x, text)}
                  <ArticleStyledContent
                    offset={x.indices[1]}
                    text={_content.slice(
                      x.indices[1],
                      entities[i + 1] ? entities[i + 1].indices[0] : undefined,
                    )}
                    richtext={richtext}
                  />
                </Fragment>
              );
            })}
          </>
        ) : (
          <ArticleStyledContent text={_content} offset={0} richtext={richtext} />
        )}
        {showViewFull ? (
          <>
            &nbsp;...
            <div className={style.entityLink}>{'View full post >'}</div>
          </>
        ) : null}
      </>
    );
  },
);

export const ArticleEntityContent = (
  props: ArticleEntityContentProps & { contentType: 'post' | 'comment' },
) => {
  return (
    <Translated
      canTranslated={!!props.article?.content_translation}
      creatorId={props.article.creator_id}
      contentType={props.contentType}
      hideManualTransBtn={!props.article?.content}>
      <MemoArticleEntityContent {...props} />
    </Translated>
  );
};

const ArticleStyledContent = memo(
  ({
    text,
    richtext,
    offset,
  }: {
    text: string[];
    offset: number;
    richtext: {
      tag: string;
      indices: [number, number];
    }[];
  }) => {
    const richTextArr = useMemo(() => {
      const textLength = text?.length || 0;
      const currentRichText: typeof richtext = [];

      richtext.some(x => {
        const [_from, _to] = x.indices;
        const [from, to] = [
          _from >= offset ? _from - offset : 0,
          _to <= textLength + offset ? _to - offset : textLength,
        ];

        if (from > textLength) {
          // 提前结束
          return true;
        }

        if (from >= 0 && to >= 0) {
          currentRichText.push({
            ...x,
            indices: [from, to],
          });
        }

        return false;
      });

      return currentRichText;
    }, [text, richtext, offset]);

    if (!text) {
      return null;
    }

    return richTextArr.length ? (
      <span>
        {richTextArr[0].indices[0] ? text.slice(0, richTextArr[0].indices[0]) : null}
        {richTextArr.map((x, i) => (
          <Fragment key={`${x.tag}_${i}`}>
            <span className={style[`is${x.tag}`]}>{text.slice(x.indices[0], x.indices[1])}</span>
            {text.slice(
              x.indices[1],
              richTextArr[i + 1] ? richTextArr[i + 1].indices[0] : undefined,
            )}
          </Fragment>
        ))}
      </span>
    ) : (
      <span>{text}</span>
    );
  },
);

type LinkParsedContentProps = {
  content?: string;
  linkClassnames?: string;
  maxLinkLength?: number;
  openUrlCallback?: (url: string) => void;
};

export const LinkParsedContentMono = ({
  content,
  linkClassnames,
  maxLinkLength = Number.MAX_SAFE_INTEGER,
  openUrlCallback,
}: LinkParsedContentProps) => {
  const urls = useMemo(() => (content ? extractUrlsIndices(content) : []), [content]);
  const { checkAndOpenUrl } = useUrl();

  const clickUrlHandler = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      e.preventDefault();

      const target = e.currentTarget.dataset?.target;
      if (target) {
        if (openUrlCallback) {
          openUrlCallback(target);
        } else {
          checkAndOpenUrl(omitRefCode(withHttp(target)));
        }
      }
    },
    [checkAndOpenUrl, openUrlCallback],
  );

  return urls?.length ? (
    <>
      {content!.slice(0, urls[0].indices[0])}
      {urls.map((x, i) => (
        <Fragment key={`${i}_${x.url}`}>
          <span
            className={cx(style.link, linkClassnames)}
            onClick={clickUrlHandler}
            data-target={x.url}>
            {x.indices[1] - x.indices[0] > maxLinkLength
              ? `${content!.slice(x.indices[0], x.indices[0] + maxLinkLength)}...`
              : content!.slice(x.indices[0], x.indices[1])}
          </span>
          {content!.slice(x.indices[1], urls[i + 1]?.indices[0])}
        </Fragment>
      ))}
    </>
  ) : (
    <>{content || null}</>
  );
};

const TranslateLinkParasedContent = ({
  content,
  translatedContent,
  ...rest
}: LinkParsedContentProps & {
  translatedContent: string;
}) => {
  const on = useTranslateOpen();

  return <LinkParsedContentMono {...rest} content={on ? translatedContent : content} />;
};

export const LinkParsedContent = ({
  translatedContent,
  creatorId,
  hideManualTransBtn,
  ...rest
}: LinkParsedContentProps & {
  creatorId?: string;
  translatedContent?: string | null;
  hideManualTransBtn?: boolean;
}) => {
  return (
    <Translated
      canTranslated={!!translatedContent}
      creatorId={creatorId}
      btnClassName={style.translateBtn}
      hideManualTransBtn={hideManualTransBtn}
      contentType="message">
      {translatedContent ? (
        <TranslateLinkParasedContent translatedContent={translatedContent} {...rest} />
      ) : (
        <LinkParsedContentMono {...rest} />
      )}
    </Translated>
  );
};

export const CompatibleEntityContent = ({
  content,
  entities,
  entities_translation,
  content_translation,
  creator_id,
  contentType,
}: {
  content?: string;
  entities?: FeedArticleEntity;
  entities_translation?: FeedArticleEntity;
  content_translation?: string;
  creator_id: string;
  contentType: 'post' | 'comment';
}) => {
  const { checkAndOpenUrl } = useUrl();

  const openUrlCallback = useCallback(
    (url: string) => {
      checkAndOpenUrl(omitRefCode(withHttp(url)));
    },
    [checkAndOpenUrl],
  );

  if (!content) {
    return null;
  }

  return (
    <ArticleEntityContent
      article={{
        content,
        // avoid unusual entities
        entities: { ...entities, tags: [] },
        entities_translation,
        content_translation,
        creator_id,
      }}
      openUrl={openUrlCallback}
      contentType={contentType}
    />
  );
};

export const ArticleEntityContentUrlEnabled = ({
  article,
  maxDisplay,
}: {
  article: Pick<
    FeedArticle,
    'content' | 'entities' | 'content_translation' | 'entities_translation' | 'creator_id'
  >;
  maxDisplay?: number;
}) => {
  const { checkAndOpenUrl } = useUrl();

  const openUrlCallback = useCallback(
    (url: string) => {
      checkAndOpenUrl(omitRefCode(withHttp(url)));
    },
    [checkAndOpenUrl],
  );

  return (
    <div className={style.articleContent}>
      <ArticleEntityContent
        article={article}
        openUrl={openUrlCallback}
        maxDisplay={maxDisplay}
        contentType="post"
      />
    </div>
  );
};
